import React, { useEffect, useState, useRef, useMemo } from 'react';
import { LocationDTO } from '@peartherapeutics/weaver-api-client';

import { AxiosResponse } from 'axios';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';

import ErrorInterceptor from 'components/common/ErrorInterceptor';
import AuthorizationComponent from 'components/common/AuthorizationComponent';
import { hasRequiredScope, scopes } from 'utils/scope';
import {
  KakarikoGridHeaderCell,
  KakarikoGridRow,
  KakarikoEditingCell,
  KakarikoGrid,
  KakarikoSelect,
  KakarikoText,
  Validations,
  KakarikoGridHeader,
} from 'library';
import { usaStates } from '../../library/Inputs';
import { AddLocation } from './AddLocation';
import './styles.scss';

export const updateLocationSuccessMessage = 'Location was updated successfully';

export const retrieveLocations = async (): Promise<
  AxiosResponse<LocationDTO[]>
> => {
  return WeaverApiClientInstance.getAllLocations();
};

export const HeaderLabels = {
  name: 'Location Name',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip: 'Postal Code',
  ref: 'External Ref',
  action: 'Actions',
};

export const ManageLocations = (props: IScreenProps) => {
  const { currentUserScopes } = props;

  const [locations, setLocationsState] = useState<LocationDTO[]>([]);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hasLocationReadScope = useRef(
    hasRequiredScope(currentUserScopes)(scopes.LOCATION_READ),
  );

  const getAllLocations = async () => {
    if (!hasLocationReadScope.current) return;

    // Server side pagination was removed as pat of WB-3488 since it is not supported when using Facade.
    try {
      setIsLoading(true);
      const { data: locations } = await retrieveLocations();
      setLocationsState([...locations]);
    } catch (e) {
      ErrorInterceptor.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllLocations();
  }, []);

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  const handleSaveLocation = async (data: UpdateLocationDTO) => {
    let updatedLocation: Nullable<LocationDTO>;
    try {
      const response = await WeaverApiClientInstance.updateLocation({
        ...data,
      });
      updatedLocation = response.data;
    } catch (e) {
      updatedLocation = null;
      ErrorInterceptor.error({ errors: [e.response.data] });
    }

    if (updatedLocation) {
      ErrorInterceptor.success(updateLocationSuccessMessage, true);
      return true;
    }
    return false;
  };

  const gridHeaders = useMemo(
    () => (
      <KakarikoGridHeader>
        <KakarikoGridHeaderCell fractionWidth={3} sortName={'name'}>
          Location Name
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'addressLine1'}>
          Address
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'city'}>
          City
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell sortName={'district'}>
          State
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'postalCode'}>
          Postal Code
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'externalRef'}>
          External Ref
        </KakarikoGridHeaderCell>
        <AuthorizationComponent requiredScope={scopes.LOCATION_WRITE}>
          <KakarikoGridHeaderCell fractionWidth={2}>
            Actions
          </KakarikoGridHeaderCell>
        </AuthorizationComponent>
      </KakarikoGridHeader>
    ),
    [],
  );

  const rowTemplate = (location: LocationDTO) => (
    <KakarikoGridRow data={location} handleDataUpdate={handleSaveLocation}>
      <KakarikoText name={'name'} validation={[Validations.isNotEmpty]} />
      <KakarikoText
        names={['addressLine1', 'addressLine2']}
        validation={[[Validations.isNotEmpty], []]}
      />
      <KakarikoText name={'city'} validation={[Validations.isNotEmpty]} />
      <KakarikoSelect
        name={'district'}
        options={usaStates.map((state) => state.abbreviation)}
        validation={[Validations.isNotEmpty]}
      />
      <KakarikoText
        name={'postalCode'}
        validation={[Validations.isValidPostalCode]}
      />
      <KakarikoText name={'externalRef'} />
      <AuthorizationComponent requiredScope={scopes.LOCATION_WRITE}>
        <KakarikoEditingCell />
      </AuthorizationComponent>
    </KakarikoGridRow>
  );

  return (
    <div data-testid="manage-locations" styleName="manage-locations-wrapper">
      <AuthorizationComponent requiredScope={scopes.LOCATION_WRITE}>
        <div styleName="search-location-wrapper">
          <AddLocation
            isDisabled={false}
            handleSubmit={() => getAllLocations()}
          />
        </div>
      </AuthorizationComponent>
      <AuthorizationComponent requiredScope={scopes.LOCATION_READ}>
        <KakarikoGrid
          data={locations}
          header={gridHeaders}
          isLoading={isLoading}
          isPaginated
          rowTemplate={rowTemplate}
          hasStickyHeader
          tableTheme={'grey'}
        />
      </AuthorizationComponent>
    </div>
  );
};
